import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, CheckCircle, XCircle } from 'lucide-react';
export function ReasoningQualityPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1751" data-mp="3s5yg7fklphms">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1752" data-mp="9xpgk038lbj9">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1753" data-mp="5x5goora3ttf">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1754" data-mp="3n5sq43tzc36t">
            <span className="w-8 h-px bg-teal" data-id="element-1755" data-mp="3ieaqbj3p08nf"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1756" data-mp="3ichgjuxs98b7">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1757" data-mp="2qmutyxcc37k0"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1758" data-mp="2qmmj1azrucbg">
            Reasoning Quality
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1759" data-mp="avaigr6226pw">
            At SyNeural, reasoning quality refers to the ability to evaluate
            claims clearly, weigh evidence appropriately, recognize
            contradictions, distinguish strong arguments from weak ones, and
            process complexity without collapsing into confusion, impulse, or
            oversimplification.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1760" data-mp="3t1fc9j5yn3qi">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1761" data-mp="1vdy31qp7u0n">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1762" data-mp="39dfh9z1dndlq">
            It is one of the most foundational human capabilities because it
            shapes how people interpret information, form conclusions, and make
            decisions across all areas of life.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1763" data-mp="3ecs96y73vav6">
            Reasoning quality is not just a measure of intelligence or
            education. A person may be knowledgeable, verbal, or credentialed
            and still reason poorly under pressure, emotion, ideology, or social
            influence. Strong reasoning involves clarity, structure, evidence
            sensitivity, and the ability to remain grounded when facing
            uncertainty, disagreement, or competing narratives.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1764" data-mp="26wubu77n6lxb">
        <div className="max-w-4xl mx-auto" data-id="element-1765" data-mp="22vsksdnvpg6f">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1766" data-mp="1stexoc2xs6b1">
            <div className="flex items-center gap-4 mb-6" data-id="element-1767" data-mp="1nshepxa3r88h">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1768" data-mp="2bl7njxmplbej">
                <Target size={24} data-id="element-1769" data-mp="2ghy2uiqow2yw" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1770" data-mp="21m0albjvziry">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1771" data-mp="26js2i5kj74w5">
              Reasoning quality affects nearly everything else in the SyNeural
              framework. It influences how people judge risk, interpret events,
              respond to persuasion, handle disagreement, and decide what is
              true, useful, or responsible. Weak reasoning can lead to
              confusion, poor judgment, narrative capture, emotional
              overreaction, and avoidable mistakes. Strong reasoning supports
              better decisions, greater independence of thought, and a more
              reliable foundation for growth and responsibility.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Reasoning Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1772" data-mp="1pcaimrpf2og0">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1773" data-mp="1kdqymwudvmqa">
          <div className="flex items-center gap-4 mb-6" data-id="element-1774" data-mp="3zqz3mzg9z3k">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1775" data-mp="3y67o68fmw9x">
              <CheckCircle size={24} data-id="element-1776" data-mp="2fonosf6ftg3w" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1777" data-mp="2kl5vvlp8pqje">
              What Strong Reasoning Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1778" data-mp="2pj7bfwngj7q4">
            Strong reasoning involves asking whether a claim is supported,
            whether evidence is strong or weak, whether assumptions are being
            smuggled in, and whether conclusions actually follow from the facts
            presented. It also includes the ability to tolerate complexity
            without reaching for simplistic answers too quickly. People with
            stronger reasoning quality are better able to track nuance,
            recognize when they do not know enough, and update their views when
            better information appears.
          </p>
        </motion.div>
      </section>

      {/* What Weak Reasoning Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1779" data-mp="1avcls3rp0w3i">
        <div className="max-w-4xl mx-auto" data-id="element-1780" data-mp="16uk9t2rfsd3j">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1781" data-mp="16vvbmx1bnas0">
            <div className="flex items-center gap-4 mb-6" data-id="element-1782" data-mp="1pt4w8hiduuau">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1783" data-mp="1psn2brj70s63">
                <XCircle size={24} data-id="element-1784" data-mp="23mnug2fn3gxh" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1785" data-mp="1yl6v4bct02j0">
                What Weak Reasoning Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1786" data-mp="2dgd519f0slhm">
              Weak reasoning often shows up as contradiction blindness,
              emotional substitution for evidence, simplistic thinking, poor
              causal understanding, and overconfidence in shaky conclusions. It
              may also appear as a tendency to accept arguments because they
              feel satisfying, socially convenient, or identity-reinforcing
              rather than because they are well-supported. In practical terms,
              weak reasoning reduces reliability in learning, civic judgment,
              work, and leadership.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1787" data-mp="1zhtto5794u5j">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1788" data-mp="24fc3dxt3fdel">
          <div className="flex items-center gap-4 mb-6" data-id="element-1789" data-mp="29zyybuh5coj8">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1790" data-mp="29xcplriis2zf">
              <Brain size={24} data-id="element-1791" data-mp="20z0qf13l7wvd" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1792" data-mp="20xpluz2bil37">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1793" data-mp="1r0tdctcf5yyn">
            Reasoning quality can be improved through logic training, argument
            analysis, better reading habits, evidence evaluation, structured
            debate, exposure to complexity, and repeated practice in
            distinguishing signal from noise. It also improves when people learn
            to slow down, question assumptions, and separate emotional reaction
            from analytical judgment. In the SyNeural framework, reasoning
            quality is treated not as a fixed trait, but as a capability that
            can be developed over time.
          </p>
        </motion.div>
      </section>
    </div>;
}